import { supabase } from '../lib/supabase';

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj) return obj;
  
  const converted = {};
  Object.keys(obj)?.forEach(key => {
    const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
    converted[camelKey] = obj?.[key];
  });
  return converted;
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj) return obj;
  
  const converted = {};
  Object.keys(obj)?.forEach(key => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    converted[snakeKey] = obj?.[key];
  });
  return converted;
};

export const supplierAccountService = {
  /**
   * Create a new supplier account application
   */
  async createAccount(accountData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // First, get or create supplier record
      const { data: supplier, error: supplierError } = await supabase?.from('suppliers')?.select('id')?.eq('contact_email', accountData?.businessEmail)?.single();

      let supplierId = supplier?.id;

      if (!supplier) {
        const { data: newSupplier, error: createError } = await supabase?.from('suppliers')?.insert({
            name: accountData?.businessName,
            contact_email: accountData?.businessEmail,
            contact_phone: accountData?.businessPhone || null
          })?.select('id')?.single();

        if (createError) throw createError;
        supplierId = newSupplier?.id;
      }

      // Create supplier account
      const dbData = {
        supplier_id: supplierId,
        user_id: user?.id,
        business_email: accountData?.businessEmail,
        business_phone: accountData?.businessPhone || null,
        fulfillment_capacity: accountData?.fulfillmentCapacity || 0,
        specialization: accountData?.specialization || [],
        account_status: 'pending'
      };

      const { data, error } = await supabase?.from('supplier_accounts')?.insert(dbData)?.select()?.single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error creating supplier account:', error);
      throw error;
    }
  },

  /**
   * Get current user's supplier account
   */
  async getCurrentAccount() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('supplier_accounts')?.select(`
          *,
          supplier:suppliers (
            id,
            name,
            contact_email,
            contact_phone,
            logo_url,
            rating
          )
        `)?.eq('user_id', user?.id)?.single();

      if (error) {
        if (error?.code === 'PGRST116') return null; // No account found
        throw error;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error fetching supplier account:', error);
      throw error;
    }
  },

  /**
   * Update supplier account details
   */
  async updateAccount(accountId, updates) {
    try {
      const dbUpdates = toSnakeCase(updates);
      dbUpdates.updated_at = new Date()?.toISOString();

      const { data, error } = await supabase?.from('supplier_accounts')?.update(dbUpdates)?.eq('id', accountId)?.select()?.single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error updating supplier account:', error);
      throw error;
    }
  },

  /**
   * Get all pending supplier applications (admin only)
   */
  async getPendingApplications() {
    try {
      const { data, error } = await supabase?.from('supplier_accounts')?.select(`
          *,
          supplier:suppliers (
            id,
            name,
            contact_email,
            contact_phone,
            rating
          ),
          user:users (
            email,
            first_name,
            last_name
          )
        `)?.eq('account_status', 'pending')?.order('created_at', { ascending: false });

      if (error) throw error;
      return data?.map(toCamelCase);
    } catch (error) {
      console.error('Error fetching pending applications:', error);
      throw error;
    }
  },

  /**
   * Approve supplier account (admin only)
   */
  async approveAccount(accountId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('supplier_accounts')?.update({
          account_status: 'active',
          approved_by: user?.id,
          approved_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })?.eq('id', accountId)?.select()?.single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error approving account:', error);
      throw error;
    }
  },

  /**
   * Reject supplier account (admin only)
   */
  async rejectAccount(accountId, reason) {
    try {
      const { data, error } = await supabase?.from('supplier_accounts')?.update({
          account_status: 'inactive',
          updated_at: new Date()?.toISOString()
        })?.eq('id', accountId)?.select()?.single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error rejecting account:', error);
      throw error;
    }
  },

  /**
   * Get supplier fulfillment statistics
   */
  async getStatistics(accountId) {
    try {
      const { data, error } = await supabase?.from('supplier_accounts')?.select('total_orders_fulfilled, rating, fulfillment_capacity, average_processing_time')?.eq('id', accountId)?.single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      throw error;
    }
  }
};